import React, { useState, useRef, useEffect, useMemo } from "react";
import Chip from "../../Chip/Chip";
import Avatar from "../../Common/Avatar";

const CustomToMailIds = ({
  value = [],
  onChange,
  placeholder = "Enter text...",
  size = "md",
  rounded = "rounded",
  disabled = false,
  maxCount = 5,
  userList = [
    {
      id: 1,
      name: "John Doe",
      email: "jonh@gmail.com",
    },
    {
      id: 2,
      name: "Jane Smith",
      email: "jane@gmail.com",
    },
    {
      id: 3,
      name: "Bob Johnson",
      email: "bob@gmail.com",
    },
  ],
  shadow = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef(null);
  const suggestionRef = useRef(null);

  // Convert email list to user objects - this is derived state, not stored state
  const selectedUsers = useMemo(() => {
    if (!value || !Array.isArray(value)) {
      return [];
    }

    return value.map((email, index) => {
      const user = userList.find((u) => u.email === email);
      return user
        ? { id: user.id, name: user.name, email: user.email }
        : {
            id: `custom-${email}-${index}`,
            name: email,
            email: email,
            isCustom: true,
          };
    });
  }, [value, userList]);

  // Filter users based on input
  const filteredUsers = useMemo(() => {
    if (inputValue.trim() === "") {
      return [];
    }

    return userList.filter(
      (user) =>
        (user.name.toLowerCase().includes(inputValue.toLowerCase()) ||
          user.email.toLowerCase().includes(inputValue.toLowerCase())) &&
        !value.some((email) => email.toLowerCase() === user.email.toLowerCase())
    );
  }, [inputValue, userList, value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    if (disabled) return;
    setInputValue(e.target.value);
    setShowSuggestions(true);
  };

  const isEmailDuplicate = (email) => {
    return value.some(
      (existingEmail) => existingEmail.toLowerCase() === email.toLowerCase()
    );
  };

  const handleSelectUser = (user) => {
    if (disabled || isEmailDuplicate(user.email) || value.length >= maxCount) {
      setInputValue("");
      setShowSuggestions(false);
      return;
    }

    const newEmails = [...value, user.email];
    onChange && onChange(newEmails);
    setInputValue("");
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      handleCreateCustomChip();
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      const newEmails = value.slice(0, -1);
      onChange && onChange(newEmails);
    }
  };

  const handleCreateCustomChip = () => {
    const email = inputValue.trim();
    if (!email) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert("Please enter a valid email address");
      return;
    }

    if (isEmailDuplicate(email)) {
      alert("This email is already added");
      setInputValue("");
      return;
    }

    if (value.length >= maxCount) {
      alert(`Maximum ${maxCount} emails allowed`);
      setInputValue("");
      return;
    }

    const newEmails = [...value, email];
    onChange && onChange(newEmails);
    setInputValue("");
    setShowSuggestions(false);
  };

  const handleDeleteChip = (emailToDelete) => {
    if (disabled) return;
    const newEmails = value.filter((email) => email !== emailToDelete);
    onChange && onChange(newEmails);
  };

  const handleInputFocus = () => {
    if (disabled) return;
    if (inputValue.trim() !== "") {
      setShowSuggestions(true);
    }
  };

  const getDisplayLabel = (user) => {
    if (user.isCustom) {
      return user.email;
    } else {
      const domain = user.email.split("@")[1] || "";
      return `${user.name} (${domain})`;
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return "px-2 py-1 text-xs";
      case "lg":
        return "px-4 py-3 text-base";
      case "md":
      default:
        return "px-3 py-2 text-sm";
    }
  };

  const getRoundedStyles = () => {
    switch (rounded) {
      case "none":
        return "rounded-none";
      case "sm":
        return "rounded-sm";
      case "md":
        return "rounded-md";
      case "lg":
        return "rounded-lg";
      case "xl":
        return "rounded-xl";
      case "full":
        return "rounded-full";
      case "rounded":
      default:
        return "rounded-md";
    }
  };

  return (
    <div className="relative w-full">
      {/* To Field */}
      <div
        className={`flex flex-wrap items-center border gap-1.5 ${getSizeStyles()} ${getRoundedStyles()}
        ${
          shadow
            ? "border-gray-50 shadow-[0_0_4px_rgba(71,71,71,0.25)]"
            : "border-gray-300"
        }
        ${
          disabled
            ? `bg-neutrals-200 cursor-not-allowed border-neutrals-200 text-black`
            : "bg-white hover:border-1 hover:border-primary focus-within:border-primary"
        }`}
      >
        {selectedUsers.map((user) => (
          <Chip
            variant="outlined"
            key={`${user.email}-${user.id}`}
            label={getDisplayLabel(user)}
            onDelete={disabled ? undefined : () => handleDeleteChip(user.email)}
            disabled={disabled}
            size="xs"
            className="bg-gray-50 !text-black border-info-100 !rounded !py-0"
          />
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          disabled={disabled || value.length >= maxCount}
          placeholder={
            value.length === 0
              ? placeholder
              : value.length >= maxCount
              ? `Maximum ${maxCount} emails allowed`
              : ""
          }
          className={`flex-1 min-w-[120px] border-none outline-none bg-transparent ${
            size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm"
          } ${
            disabled || value.length >= maxCount ? "cursor-not-allowed" : ""
          }`}
        />
      </div>

      {/* Suggestions Dropdown */}
      {!disabled && showSuggestions && filteredUsers.length > 0 && (
        <div
          className="absolute top-full left-0 w-full max-w-[350px] max-h-[200px] overflow-y-auto bg-white border border-gray-300 rounded-md z-10 mt-1 shadow-lg"
          ref={suggestionRef}
        >
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center p-2 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => handleSelectUser(user)}
            >
              <Avatar src={user.avatar} size="sm" className="mr-2.5" />
              <div className="flex flex-col">
                <div className="text-sm font-medium">{user.name}</div>
                <div className="text-xs text-gray-600">{user.email}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomToMailIds;
